/**
 * RightPanelReview — Single-document review panel with editable fields.
 *
 * Layout:
 *  1. Header — "REVIEW PROPERTIES" + status badge (with dot)
 *  2. Extracted Fields — Editable custom fields from DocumentDetailDto
 *  3. Actions — Export (disabled until required fields populated) + Delete
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { parseDate } from "@internationalized/date";
import { AlertCircle, AlertTriangle, ChevronDown, File06, FileCheck02, Loading01, RefreshCw05, Save01, Trash01, Users01 } from "@untitledui/icons";
import type { DateValue } from "react-aria-components";
import { DialogTrigger as AriaDialogTrigger, Heading as AriaHeading } from "react-aria-components";
import { DatePicker } from "@/components/application/date-picker/date-picker";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icons";
import { BackgroundPattern } from "@/components/shared-assets/background-patterns";
import { useDocumentPresence } from "@/hooks/use-document-presence";
import { useGlSuggestions } from "@/hooks/use-gl-suggestions";
import { getErrorDisplayMessage } from "@/lib/error-message-parser";
import { ApiException } from "@/services/api";
import { useDocumentsApi } from "@/services/api/documents-api";
import { ExceptionCodeEnum } from "@/types/api/common";
import {
    type CustomFieldConflictDto,
    type CustomFieldDefinitionDto,
    type CustomFieldValueDto,
    type DocumentDetailDto,
    FieldTypeEnum,
    type LineItemDto,
    type UpdateCustomFieldsRequestDto,
    getCustomFieldDtoDisplayValue,
} from "@/types/api/documents";
import { GL_CODE_FIELD_CODE, type GlCodeDto, type GlSuggestionDto } from "@/types/api/gl-codes";
import type { OrganizationListItemDto } from "@/types/api/organizations";
import type { RoutingCodeDto } from "@/types/api/routing-codes";
import type { HomeDocument } from "@/types/home";
import { cx } from "@/utils/cx";
import { getStatusBadgeColor, getStatusLabel } from "./helpers";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EditedValue = string | number | boolean | DateValue | null;

interface EditedField {
    fieldType: string;
    value: EditedValue;
    /** When true, the field value failed validation on the server. Cleared when user edits the field. */
    containsError?: boolean;
    /**
     * Optimistic-concurrency baseline: the stored value's updatedAt snapshotted when the user
     * FIRST edited this field (null = no stored value existed then). Snapshotting at first edit
     * — not at save — matters: silentRefresh may replace base values underneath an in-progress
     * edit, and the conflict window must start where the user's editing started.
     */
    baseUpdatedAt?: string | null;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RightPanelReviewHandle {
    save: () => Promise<void>;
}

interface RightPanelReviewProps {
    /** Currently active document (null when nothing selected) */
    document: HomeDocument | null;
    /** Full document detail from API (has custom fields, pages, etc.) */
    documentDetail: DocumentDetailDto | null;
    /** Whether detail is being fetched */
    isDetailLoading: boolean;
    /** Custom field definitions (includes isRequired) */
    customFieldDefinitions: CustomFieldDefinitionDto[];
    /** LineItem-scoped field definitions for the document type's line item types */
    lineItemFieldDefinitions: CustomFieldDefinitionDto[];
    /** Routing codes for dropdown (when fieldCode is routing_code and entitlement enabled) */
    routingCodes?: RoutingCodeDto[];
    /** GL chart of accounts for the per-line gl_code dropdown (when entitlement enabled) */
    glCodes?: GlCodeDto[];
    /** Whether GL code suggestions are enabled for this client (FEATURE_GL_SUGGEST) */
    glSuggestionsEnabled?: boolean;
    /** Organizations scoped to the current user. Dropdown shown only when length > 1. */
    orgs?: OrganizationListItemDto[];
    /** Called when user selects a different organization for this document */
    onUpdateOrganization?: (docId: number, organizationId: number) => Promise<void>;
    /** Whether the current session is an admin previewing a client */
    isAdminPreview?: boolean;
    /** Called when user clicks Export (saves fields first, then exports) */
    onApprove: (docId: number) => void;
    /** Called when user deletes the document */
    onDelete: (docId: number) => void;
    /** Update custom fields on the document */
    onUpdateFields: (docId: number, dto: UpdateCustomFieldsRequestDto) => Promise<void>;
    /** CSV export: generates CSV download + marks status as Exported */
    onCsvExport: (doc: DocumentDetailDto) => Promise<void>;
    /** External export: pushes document to bridge via backend */
    onExternalExport: (docId: number) => Promise<void>;
    /** Retry a failed external export (latest job is a failed ExternalExport) */
    onRetryExport: (docId: number) => Promise<void>;
    /** Re-trigger vendor lookup for a document whose lookup failed (BizVendorNotFound) */
    onRelookup: (docId: number) => Promise<void>;
    /** Field code of the vendor-name field; in failure mode only this field is shown for editing */
    vendorNameFieldCode?: string | null;
    /** Whether Direct Sync export is offered (bridge exists AND export mode is DirectSync) */
    canDirectSync: boolean;
    /** Notify parent when hasChanges status changes */
    onHasChangesChange?: (hasChanges: boolean) => void;
    /** Called when user clicks the duplicate file link — selects that file in the left panel */
    onSelectDuplicateFile?: (fileStorageId: number) => void;
    /** Re-fetch the document detail (used by the concurrency-conflict "Reload" action) */
    onReloadDetail?: (docId: number) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isoStringToDateValue(isoString: string | null | undefined): DateValue | null {
    if (!isoString) return null;
    try {
        const datePart = isoString.split("T")[0];
        return parseDate(datePart);
    } catch {
        return null;
    }
}

function dateValueToIsoString(dateValue: DateValue | null | unknown): string | null {
    if (!dateValue) return null;
    if (typeof dateValue === "object" && dateValue !== null && "toDate" in dateValue && typeof (dateValue as DateValue).toDate === "function") {
        try {
            const dateVal = dateValue as DateValue;
            const year = dateVal.year;
            const month = String(dateVal.month).padStart(2, "0");
            const day = String(dateVal.day).padStart(2, "0");
            return `${year}-${month}-${day}T00:00:00Z`;
        } catch {
            return null;
        }
    }
    return null;
}

function isEmptyValue(value: EditedValue | undefined): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.trim() === "";
    if (typeof value === "number") return isNaN(value);
    if (typeof value === "boolean") return false;
    if (typeof value === "object" && "toDate" in value) return false;
    return true;
}

/** Composite key for line-item field state maps: one entry per (lineItem, definition). */
function lineItemKey(lineItemId: number, definitionId: number): string {
    return `${lineItemId}:${definitionId}`;
}

/** Build the editable representation of an API field value (dates become DateValue). */
function toEditedField(fieldType: string, fieldValue: CustomFieldValueDto | undefined): EditedField {
    if (!fieldValue) return { fieldType, value: null };
    const currentValue = getCustomFieldDtoDisplayValue(fieldValue);
    if ((fieldType === FieldTypeEnum.Date || fieldType === "Date") && typeof currentValue === "string") {
        return { fieldType, value: isoStringToDateValue(currentValue), containsError: fieldValue.containsError };
    }
    return { fieldType, value: currentValue, containsError: fieldValue.containsError };
}

/** Whether an edited field differs from its base (date-aware comparison). */
function editedFieldDiffers(edited: EditedField, base: EditedField): boolean {
    const fieldType = edited.fieldType;
    if (fieldType === FieldTypeEnum.Date || fieldType === "Date") {
        const baseDate = base.value && typeof base.value === "object" && "toDate" in base.value ? (base.value as DateValue) : null;
        const editedDate = edited.value && typeof edited.value === "object" && "toDate" in edited.value ? (edited.value as DateValue) : null;
        if (baseDate === null && editedDate === null) return false;
        if (baseDate === null || editedDate === null) return true;
        return baseDate.year !== editedDate.year || baseDate.month !== editedDate.month || baseDate.day !== editedDate.day;
    }
    return base.value !== edited.value;
}

/** Whether an edited value differs from the original API value (date-aware comparison). */
function valueHasChanged(fieldType: string, originalValue: string | number | boolean | null, editedValue: EditedValue): boolean {
    if (fieldType === FieldTypeEnum.Date || fieldType === "Date") {
        const originalDateValue = isoStringToDateValue(originalValue as string | null);
        const editedDateValue = editedValue && typeof editedValue === "object" && "toDate" in editedValue ? (editedValue as DateValue) : null;
        if (originalDateValue === null && editedDateValue === null) return false;
        if (originalDateValue === null || editedDateValue === null) return true;
        return (
            originalDateValue.year !== editedDateValue.year ||
            originalDateValue.month !== editedDateValue.month ||
            originalDateValue.day !== editedDateValue.day
        );
    }
    return originalValue !== editedValue;
}

/** Convert an edited value to the API wire format (string, or null to delete). */
function editedValueToString(fieldType: string, value: EditedValue): string | null {
    switch (fieldType) {
        case FieldTypeEnum.String:
        case FieldTypeEnum.Integer:
        case FieldTypeEnum.Decimal:
            return value !== null && value !== undefined ? String(value) : null;
        case FieldTypeEnum.Date:
            return dateValueToIsoString(value);
        case FieldTypeEnum.Boolean:
            if (value === true) return "true";
            if (value === false) return "false";
            return null;
        default:
            return null;
    }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const RightPanelReview = forwardRef<RightPanelReviewHandle, RightPanelReviewProps>(function RightPanelReview(
    {
        document,
        documentDetail,
        isDetailLoading,
        customFieldDefinitions,
        lineItemFieldDefinitions,
        routingCodes,
        glCodes,
        glSuggestionsEnabled,
        orgs,
        onApprove: _onApprove,
        onDelete,
        onUpdateFields,
        onUpdateOrganization: _onUpdateOrganization,
        isAdminPreview,
        onCsvExport,
        onExternalExport,
        onRetryExport,
        onRelookup,
        vendorNameFieldCode,
        canDirectSync,
        onHasChangesChange,
        onSelectDuplicateFile,
        onReloadDetail,
    },
    ref,
) {
    const [isSaving, setIsSaving] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [updateError, setUpdateError] = useState<string | null>(null);
    /** Fields rejected with 409 BizConcurrentUpdateConflict on the last save (null = no conflict) */
    const [conflicts, setConflicts] = useState<CustomFieldConflictDto[] | null>(null);
    const [isRelooking, setIsRelooking] = useState(false);
    const [relookupError, setRelookupError] = useState<string | null>(null);
    const [_hasExported, setHasExported] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);
    const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
    const [showAdminExportWarning, setShowAdminExportWarning] = useState(false);

    // Presence: heartbeat "I am viewing this document" while it is open and show how many
    // other users have it open. Informational only — never blocks editing or export.
    const otherViewerCount = useDocumentPresence(document?.id ?? null);

    // Base field values derived directly from props (no async timing issues)
    const baseFieldValues = useMemo((): Map<number, EditedField> => {
        if (!documentDetail || !customFieldDefinitions.length) return new Map();
        if (documentDetail.id !== document?.id) return new Map();

        const values = new Map<number, EditedField>();
        for (const definition of customFieldDefinitions) {
            const fieldValue = documentDetail.customFieldValues?.find((fv) => fv.customFieldDefinitionId === definition.id);
            values.set(definition.id, toEditedField(definition.fieldType, fieldValue));
        }
        return values;
    }, [documentDetail, customFieldDefinitions, document?.id]);

    // Track only user edits (key prop on component resets this on document switch)
    const [userEdits, setUserEdits] = useState<Map<number, EditedField>>(new Map());

    // Line-item field state — same base/edits/merge model as document fields, but keyed by
    // `${lineItemId}:${definitionId}` since the same definition repeats across lines.
    const lineItems = useMemo((): LineItemDto[] => {
        if (!documentDetail || documentDetail.id !== document?.id) return [];
        return [...(documentDetail.lineItems ?? [])].sort((a, b) => a.lineNumber - b.lineNumber);
    }, [documentDetail, document?.id]);

    const lineItemBaseValues = useMemo((): Map<string, EditedField> => {
        if (!lineItems.length || !lineItemFieldDefinitions.length) return new Map();

        const values = new Map<string, EditedField>();
        for (const lineItem of lineItems) {
            for (const definition of lineItemFieldDefinitions) {
                const fieldValue = lineItem.customFieldValues?.find((fv) => fv.customFieldDefinitionId === definition.id);
                values.set(lineItemKey(lineItem.id, definition.id), toEditedField(definition.fieldType, fieldValue));
            }
        }
        return values;
    }, [lineItems, lineItemFieldDefinitions]);

    const [lineItemUserEdits, setLineItemUserEdits] = useState<Map<string, EditedField>>(new Map());

    // GL code suggestions — advisory only. They live OUTSIDE the edit state and enter it
    // exclusively through explicit user action (picking from the dropdown or the
    // "Apply suggestions" button), both of which go through handleLineItemFieldChange so
    // the concurrency baseline is captured by the untouched first-edit snapshot logic.
    // Saving is the confirmation; nothing is ever persisted without it.
    const documentsApi = useDocumentsApi();
    const glFieldDefinition = useMemo(
        (): CustomFieldDefinitionDto | null => lineItemFieldDefinitions.find((d) => d.fieldCode === GL_CODE_FIELD_CODE) ?? null,
        [lineItemFieldDefinitions],
    );
    const glSuggestionsActive = !!glSuggestionsEnabled && !!glFieldDefinition && lineItems.length > 0;
    const glSuggestionsByLine = useGlSuggestions(document?.id ?? null, glSuggestionsActive);

    // Merge: base values + user edits overlay
    const editedFieldValues = useMemo((): Map<number, EditedField> => {
        if (baseFieldValues.size === 0) return new Map();
        const merged = new Map(baseFieldValues);
        for (const [defId, field] of userEdits) {
            merged.set(defId, field);
        }
        return merged;
    }, [baseFieldValues, userEdits]);

    const editedLineItemValues = useMemo((): Map<string, EditedField> => {
        if (lineItemBaseValues.size === 0) return new Map();
        const merged = new Map(lineItemBaseValues);
        for (const [key, field] of lineItemUserEdits) {
            merged.set(key, field);
        }
        return merged;
    }, [lineItemBaseValues, lineItemUserEdits]);

    const handleFieldChange = useCallback(
        (definitionId: number, fieldType: string, value: EditedValue) => {
            setUserEdits((prev) => {
                const next = new Map(prev);
                // Concurrency baseline: snapshot the stored value's updatedAt on the FIRST edit of
                // this field and keep it for the whole edit session (later silentRefreshes must not
                // move it — the conflict window starts where the user's editing started).
                const existing = prev.get(definitionId);
                const baseUpdatedAt =
                    existing !== undefined
                        ? existing.baseUpdatedAt
                        : (documentDetail?.customFieldValues?.find((fv) => fv.customFieldDefinitionId === definitionId)?.updatedAt ?? null);
                next.set(definitionId, { fieldType, value, baseUpdatedAt });
                return next;
            });
        },
        [documentDetail],
    );

    const handleLineItemFieldChange = useCallback(
        (lineItemId: number, definitionId: number, fieldType: string, value: EditedValue) => {
            setLineItemUserEdits((prev) => {
                const next = new Map(prev);
                const key = lineItemKey(lineItemId, definitionId);
                const existing = prev.get(key);
                const baseUpdatedAt =
                    existing !== undefined
                        ? existing.baseUpdatedAt
                        : (lineItems
                              .find((li) => li.id === lineItemId)
                              ?.customFieldValues?.find((fv) => fv.customFieldDefinitionId === definitionId)?.updatedAt ?? null);
                next.set(key, { fieldType, value, baseUpdatedAt });
                return next;
            });
        },
        [lineItems],
    );

    // "Apply suggestions": promote each line's top suggestion into the edit state for GL
    // fields that are still empty. Explicit user action + Save = the brief's "top one
    // preselected, user confirms rather than composes" without ever auto-persisting.
    const hasApplicableSuggestions = useMemo((): boolean => {
        if (!glFieldDefinition || glSuggestionsByLine.size === 0) return false;
        return lineItems.some((lineItem) => {
            const top = glSuggestionsByLine.get(lineItem.id)?.[0];
            if (!top) return false;
            return isEmptyValue(editedLineItemValues.get(lineItemKey(lineItem.id, glFieldDefinition.id))?.value);
        });
    }, [glFieldDefinition, glSuggestionsByLine, lineItems, editedLineItemValues]);

    const handleApplySuggestions = useCallback(() => {
        if (!glFieldDefinition) return;
        for (const lineItem of lineItems) {
            const top = glSuggestionsByLine.get(lineItem.id)?.[0];
            if (!top) continue;
            const current = editedLineItemValues.get(lineItemKey(lineItem.id, glFieldDefinition.id));
            if (!isEmptyValue(current?.value)) continue;
            handleLineItemFieldChange(lineItem.id, glFieldDefinition.id, glFieldDefinition.fieldType, top.code);
        }
    }, [glFieldDefinition, lineItems, glSuggestionsByLine, editedLineItemValues, handleLineItemFieldChange]);

    // Validate required fields + fields with server-side validation errors
    const fieldErrors = useMemo((): Set<number> => {
        const errors = new Set<number>();
        for (const definition of customFieldDefinitions) {
            const edited = editedFieldValues.get(definition.id);
            const base = baseFieldValues.get(definition.id);
            const userHasEdited = userEdits.has(definition.id);

            // Required field validation: show error if empty (skip invisible fields — user can't fill them)
            if (definition.isRequired && definition.isVisible && isEmptyValue(edited?.value)) {
                errors.add(definition.id);
                continue;
            }

            // Server-side validation error: show error if containsError is true AND user hasn't edited
            // (if user edited the field, we optimistically clear the error state)
            if (base?.containsError && !userHasEdited) {
                errors.add(definition.id);
            }
        }
        return errors;
    }, [customFieldDefinitions, editedFieldValues, baseFieldValues, userEdits]);

    // Same validation for line-item fields, keyed by `${lineItemId}:${definitionId}`
    const lineItemFieldErrors = useMemo((): Set<string> => {
        const errors = new Set<string>();
        for (const lineItem of lineItems) {
            for (const definition of lineItemFieldDefinitions) {
                const key = lineItemKey(lineItem.id, definition.id);
                const edited = editedLineItemValues.get(key);
                const base = lineItemBaseValues.get(key);
                const userHasEdited = lineItemUserEdits.has(key);

                if (definition.isRequired && definition.isVisible && isEmptyValue(edited?.value)) {
                    errors.add(key);
                    continue;
                }

                if (base?.containsError && !userHasEdited) {
                    errors.add(key);
                }
            }
        }
        return errors;
    }, [lineItems, lineItemFieldDefinitions, editedLineItemValues, lineItemBaseValues, lineItemUserEdits]);

    const allRequiredFieldsPopulated = fieldErrors.size === 0 && lineItemFieldErrors.size === 0;

    // Check if user has made any actual changes compared to base values
    const hasChanges = useMemo((): boolean => {
        for (const [defId, editedField] of userEdits) {
            const baseField = baseFieldValues.get(defId);
            if (!baseField || editedFieldDiffers(editedField, baseField)) return true;
        }
        for (const [key, editedField] of lineItemUserEdits) {
            const baseField = lineItemBaseValues.get(key);
            if (!baseField || editedFieldDiffers(editedField, baseField)) return true;
        }
        return false;
    }, [userEdits, baseFieldValues, lineItemUserEdits, lineItemBaseValues]);

    // Notify parent when hasChanges status changes
    useEffect(() => {
        onHasChangesChange?.(hasChanges);
    }, [hasChanges, onHasChangesChange]);

    // Sync selectedOrgId when the document detail changes
    useEffect(() => {
        setSelectedOrgId(documentDetail?.organizationId ?? null);
    }, [documentDetail?.id, documentDetail?.organizationId]);

    // Export-failure mode: the push to the target system permanently failed (not a data problem).
    // Fields stay editable and the Export action becomes "Retry export" — the sanctioned recovery
    // path. Data errors (any other errorCode) still block export below.
    const isExportFailureMode = !!document?.errorCode && !!document?.isExportFailed;

    const canExport =
        !!document &&
        !!documentDetail &&
        documentDetail.id === document.id &&
        isDetailLoading == false &&
        document.isProcessingEndStateReached &&
        (document.errorCode == null || isExportFailureMode) &&
        (document.status === "ready" || document.status === "queued" || document.status === "exported" || isExportFailureMode) &&
        !document.hasFieldErrors &&
        !!allRequiredFieldsPopulated &&
        hasChanges == false &&
        isSaving == false;

    // Build the list of changed values — document fields and line-item fields join the same
    // request; line-item entries carry lineItemId so the backend targets the right entity.
    const buildFieldUpdates = useCallback((): UpdateCustomFieldsRequestDto["customFieldValues"] => {
        const fieldUpdates: UpdateCustomFieldsRequestDto["customFieldValues"] = [];

        // Document fields
        const originalValueMap = new Map<number, string | number | boolean | null>();
        if (documentDetail?.customFieldValues) {
            for (const fv of documentDetail.customFieldValues) {
                originalValueMap.set(fv.customFieldDefinitionId, getCustomFieldDtoDisplayValue(fv));
            }
        }
        for (const definition of customFieldDefinitions) {
            const editedValue = editedFieldValues.get(definition.id);
            if (!editedValue) continue;

            const originalValue = originalValueMap.get(definition.id) ?? null;
            if (!valueHasChanged(definition.fieldType, originalValue, editedValue.value)) continue;

            fieldUpdates.push({
                customFieldDefinitionId: definition.id,
                value: editedValueToString(definition.fieldType, editedValue.value),
                baseUpdatedAt: editedValue.baseUpdatedAt ?? null,
            });
        }

        // Line-item fields — same diffing per (lineItem, definition)
        for (const lineItem of lineItems) {
            const lineOriginalMap = new Map<number, string | number | boolean | null>();
            for (const fv of lineItem.customFieldValues ?? []) {
                lineOriginalMap.set(fv.customFieldDefinitionId, getCustomFieldDtoDisplayValue(fv));
            }
            for (const definition of lineItemFieldDefinitions) {
                const editedValue = editedLineItemValues.get(lineItemKey(lineItem.id, definition.id));
                if (!editedValue) continue;

                const originalValue = lineOriginalMap.get(definition.id) ?? null;
                if (!valueHasChanged(definition.fieldType, originalValue, editedValue.value)) continue;

                fieldUpdates.push({
                    customFieldDefinitionId: definition.id,
                    lineItemId: lineItem.id,
                    value: editedValueToString(definition.fieldType, editedValue.value),
                    baseUpdatedAt: editedValue.baseUpdatedAt ?? null,
                });
            }
        }

        return fieldUpdates;
    }, [documentDetail, customFieldDefinitions, editedFieldValues, lineItems, lineItemFieldDefinitions, editedLineItemValues]);

    // Save only the fields the user actually changed. Throws on API failure so callers can react
    // (e.g. relookup aborts if the save fails). Clears user edits on success — the canonical reset.
    // checkConcurrency: the backend 409s (BizConcurrentUpdateConflict) if another writer changed
    // any of THESE fields since the user started editing them; nothing is written in that case.
    const saveChangedFields = useCallback(async () => {
        if (!document) return;

        const fieldUpdates = buildFieldUpdates();
        if (fieldUpdates.length > 0) {
            await onUpdateFields(document.id, { customFieldValues: fieldUpdates, checkConcurrency: true });

            // Accept/override telemetry: for each saved gl_code line value on a line that had
            // suggestions, report what was chosen. Fire-and-forget — the save flow never
            // waits on it and never surfaces its failures.
            if (glFieldDefinition && glSuggestionsByLine.size > 0) {
                const feedbackLines = fieldUpdates
                    .filter(
                        (u) =>
                            u.customFieldDefinitionId === glFieldDefinition.id &&
                            u.lineItemId != null &&
                            glSuggestionsByLine.has(u.lineItemId),
                    )
                    .map((u) => ({ lineItemId: u.lineItemId!, chosenCode: u.value ?? null }));
                if (feedbackLines.length > 0) {
                    documentsApi.postGlSuggestionFeedback(document.id, { lines: feedbackLines }).catch(() => {});
                }
            }
        }

        // Clear user edits after successful save
        setUserEdits(new Map());
        setLineItemUserEdits(new Map());
        setConflicts(null);
    }, [document, buildFieldUpdates, onUpdateFields, glFieldDefinition, glSuggestionsByLine, documentsApi]);

    /**
     * Shared conflict triage for every path that saves fields. Returns true when the error was
     * a 409 concurrency conflict (now surfaced in the conflict banner) — callers must skip
     * their generic error message in that case.
     */
    const handleSaveConflict = useCallback((err: unknown): boolean => {
        if (err instanceof ApiException && err.code === ExceptionCodeEnum.BizConcurrentUpdateConflict) {
            const conflictingFields =
                (err.errorResponse as { conflictingFields?: CustomFieldConflictDto[] } | undefined)?.conflictingFields ?? [];
            setConflicts(conflictingFields);
            return true;
        }
        return false;
    }, []);

    // Concurrency-conflict recovery: drop the conflicting edits (their inputs revert to the
    // reloaded values) and re-fetch the document. Non-conflicting edits are kept — they were
    // just verified clean, and field-level diffing means they can still save independently.
    // No auto-merge by design: the user sees what changed and re-applies what they still want.
    const handleReloadAfterConflict = useCallback(() => {
        if (!document) return;
        const conflicted = conflicts ?? [];
        const conflictedDocDefIds = new Set(conflicted.filter((c) => c.lineItemId == null).map((c) => c.customFieldDefinitionId));
        const conflictedLineKeys = new Set(
            conflicted.filter((c) => c.lineItemId != null).map((c) => lineItemKey(c.lineItemId!, c.customFieldDefinitionId)),
        );
        setUserEdits((prev) => {
            const next = new Map(prev);
            for (const defId of conflictedDocDefIds) next.delete(defId);
            return next;
        });
        setLineItemUserEdits((prev) => {
            const next = new Map(prev);
            for (const key of conflictedLineKeys) next.delete(key);
            return next;
        });
        setConflicts(null);
        setUpdateError(null);
        onReloadDetail?.(document.id);
    }, [document, conflicts, onReloadDetail]);

    // Handle Export: save only modified fields, then export
    const handleExport = useCallback(async () => {
        if (!document || !allRequiredFieldsPopulated) return;
        setIsSaving(true);
        try {
            await saveChangedFields();

            // Export-failure mode retries the failed export job (edited values were saved above);
            // otherwise Direct Sync when offered, else CSV. A CSV-mode client never retries a
            // bridge export (the backend refuses) — a leftover failed export re-exports as CSV.
            if (document.errorCode && document.isExportFailed && canDirectSync) {
                await onRetryExport(document.id);
            } else if (canDirectSync) {
                await onExternalExport(document.id);
            } else if (documentDetail) {
                await onCsvExport(documentDetail);
            }

            setHasExported(true);
            setExportError(null);
        } catch (err) {
            console.error("Failed to export:", err);
            // Concurrent edit conflict on the pre-export save: surfaced in the conflict banner.
            if (handleSaveConflict(err)) {
                setExportError(null);
            }
            // Server-side export gate (stale-tab case): the document has unresolved blocking
            // exceptions — show the specific reason instead of a generic failure.
            else if (err instanceof ApiException && err.code === ExceptionCodeEnum.BizExportBlocked) {
                setExportError(getErrorDisplayMessage(err.code));
            } else {
                setExportError("Failed to export document. Please try again.");
            }
        } finally {
            setIsSaving(false);
        }
    }, [
        document,
        documentDetail,
        allRequiredFieldsPopulated,
        saveChangedFields,
        handleSaveConflict,
        onCsvExport,
        onExternalExport,
        onRetryExport,
        canDirectSync,
    ]);

    // Handle Update Fields: save only modified fields (no export)
    const handleUpdateFields = useCallback(async () => {
        if (!document || !hasChanges) return;

        setUpdateError(null);

        // Validate required fields (document + line items)
        if (fieldErrors.size > 0 || lineItemFieldErrors.size > 0) {
            const errorFieldLabels = customFieldDefinitions.filter((d) => fieldErrors.has(d.id)).map((d) => d.fieldLabel);
            const lineItemErrorLabels = [...lineItemFieldErrors]
                .map((key) => {
                    const [lineItemId, definitionId] = key.split(":").map(Number);
                    const lineItem = lineItems.find((li) => li.id === lineItemId);
                    const definition = lineItemFieldDefinitions.find((d) => d.id === definitionId);
                    return definition ? `Line ${lineItem?.lineNumber ?? "?"}: ${definition.fieldLabel}` : null;
                })
                .filter((label): label is string => label !== null);
            setUpdateError(`Please fix the following fields: ${[...errorFieldLabels, ...lineItemErrorLabels].join(", ")}`);
            return;
        }

        setIsUpdating(true);
        try {
            await saveChangedFields();
        } catch (err) {
            console.error("Failed to update fields:", err);
            if (!handleSaveConflict(err)) {
                setUpdateError("Failed to update fields. Please try again.");
            }
        } finally {
            setIsUpdating(false);
        }
    }, [
        document,
        hasChanges,
        fieldErrors,
        lineItemFieldErrors,
        customFieldDefinitions,
        lineItems,
        lineItemFieldDefinitions,
        saveChangedFields,
        handleSaveConflict,
    ]);

    // Handle Relookup: persist any edited fields, then re-trigger vendor lookup. The document
    // returns to a processing state and status polling resumes (reset flow unchanged).
    const handleRelookup = useCallback(async () => {
        if (!document) return;

        setRelookupError(null);

        // Failure mode shows only the vendor-name field, so validate just that field.
        const vendorCode = vendorNameFieldCode ?? "vendor_name";
        const vendorDef = customFieldDefinitions.find((d) => d.fieldCode === vendorCode);
        if (vendorDef && isEmptyValue(editedFieldValues.get(vendorDef.id)?.value)) {
            setRelookupError("Vendor name is required.");
            return;
        }

        setIsRelooking(true);
        try {
            // Save edited visible fields first; abort relookup if the save fails.
            await saveChangedFields();
            await onRelookup(document.id);
        } catch (err) {
            console.error("Failed to relookup vendor:", err);
            if (!handleSaveConflict(err)) {
                setRelookupError("Failed to re-run vendor lookup. Please try again.");
            }
        } finally {
            setIsRelooking(false);
        }
    }, [document, customFieldDefinitions, editedFieldValues, vendorNameFieldCode, saveChangedFields, handleSaveConflict, onRelookup]);

    // Handle Cancel (vendor-failure mode): discard unsaved edits, keep the document selected.
    const handleCancelVendorEdits = useCallback(() => {
        setUserEdits(new Map());
        setLineItemUserEdits(new Map());
        setRelookupError(null);
        setConflicts(null);
    }, []);

    // Expose save method to parent via ref
    useImperativeHandle(
        ref,
        () => ({
            save: handleUpdateFields,
        }),
        [handleUpdateFields],
    );

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    if (!document) {
        return (
            <div className="flex h-full flex-col items-center justify-center px-6">
                <FeaturedIcon icon={File06} color="gray" theme="outline" size="lg" />
                <p className="mt-4 text-sm font-semibold text-primary">No Document Selected</p>
                <p className="mt-1 text-center text-xs text-tertiary">Select a document to review its properties</p>
            </div>
        );
    }

    const isDetailReady = documentDetail?.id === document.id && !isDetailLoading;

    // Vendor-lookup-failure mode: the lookup finished with no ERP match. Unlike other errors, the
    // user can correct the visible fields and re-run the lookup, so fields stay editable.
    const isVendorLookupFailure = document.errorCode === ExceptionCodeEnum.BizVendorNotFound;

    return (
        <div className="flex h-full flex-col overflow-hidden">
            {/* ---- Header ---- */}
            <div className="shrink-0 border-b border-tertiary px-5 py-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-xs font-semibold tracking-wider text-quaternary uppercase">Review Properties</h2>
                    <BadgeWithDot size="sm" color={getStatusBadgeColor(document.status)}>
                        {getStatusLabel(document.status)}
                    </BadgeWithDot>
                </div>
                {/* Presence indicator — awareness only, never blocks editing/saving/exporting.
                    Hidden entirely when the user is alone (no "0 other users"). */}
                {otherViewerCount > 0 && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-warning-primary">
                        <Users01 className="size-3.5 shrink-0" aria-hidden />
                        <span>
                            {otherViewerCount} other {otherViewerCount === 1 ? "user" : "users"} viewing this document
                        </span>
                    </div>
                )}
            </div>

            {/* ---- Scrollable content ---- */}
            <div className="custom-scrollbar flex-1 space-y-8 overflow-y-auto px-5 py-6">
                {/* Vendor-lookup-failure banner — top of panel; editable fields remain below */}
                {isVendorLookupFailure && (
                    <div className="border-warning flex items-start gap-3 rounded-lg border bg-warning-secondary px-4 py-3">
                        <FeaturedIcon icon={AlertTriangle} color="warning" theme="light" size="sm" className="shrink-0" />
                        <p className="text-sm font-medium text-warning-primary">{getErrorDisplayMessage(document.errorCode)}</p>
                    </div>
                )}

                {/* Concurrency-conflict banner — the last save was rejected because another user
                    changed the listed fields since this user started editing them. Nothing was
                    written; the explicit reload path is the only recovery (no auto-merge). */}
                {conflicts && (
                    <div className="border-warning flex items-start gap-3 rounded-lg border bg-warning-secondary px-4 py-3">
                        <FeaturedIcon icon={AlertTriangle} color="warning" theme="light" size="sm" className="shrink-0" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-warning-primary">
                                Another user changed this document while you were editing. Your changes were not saved.
                            </p>
                            {conflicts.length > 0 && (
                                <ul className="mt-1 space-y-0.5 text-xs text-warning-primary/80">
                                    {conflicts.map((conflict) => {
                                        const lineNumber =
                                            conflict.lineItemId != null ? lineItems.find((li) => li.id === conflict.lineItemId)?.lineNumber : null;
                                        return (
                                            <li key={`${conflict.customFieldDefinitionId}:${conflict.lineItemId ?? "doc"}`}>
                                                {lineNumber != null ? `Line ${lineNumber} — ` : ""}
                                                <span className="font-medium">{conflict.fieldLabel || conflict.fieldCode}</span>
                                                {": now "}
                                                {conflict.currentValue != null && conflict.currentValue !== "" ? `"${conflict.currentValue}"` : "(cleared)"}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            <Button size="sm" color="secondary" className="mt-2" iconLeading={RefreshCw05} onClick={handleReloadAfterConflict}>
                                Reload latest values
                            </Button>
                            <p className="mt-1 text-xs text-tertiary">
                                Reloading shows the latest saved values for the fields above; your other edits are kept. Re-apply what you still
                                want, then save again.
                            </p>
                        </div>
                    </div>
                )}

                {/* Export-failure banner — the push to the ERP failed; the document's data is fine.
                    Fields remain editable below and the Export action becomes "Retry export". */}
                {isExportFailureMode && !isVendorLookupFailure && (
                    <div className="flex items-start gap-3 rounded-lg border border-error bg-error-secondary px-4 py-3">
                        <FeaturedIcon icon={AlertCircle} color="error" theme="light" size="sm" className="shrink-0" />
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-error-primary">Export failed. {getErrorDisplayMessage(document.errorCode)}</p>
                            {documentDetail?.id === document.id && documentDetail?.errorMessage && (
                                <p className="mt-1 text-xs break-words text-error-primary/80">{documentDetail.errorMessage}</p>
                            )}
                            <p className="mt-1 text-xs text-tertiary">You can correct fields if needed, then retry the export below.</p>
                        </div>
                    </div>
                )}

                {/* Custom Fields (Editable) */}
                {isDetailLoading && (
                    <div className="flex items-center gap-2 text-tertiary">
                        <Loading01 className="size-4 animate-spin" />
                        <span className="text-xs">Loading fields...</span>
                    </div>
                )}

                {/* Branch 1: Processing — document still being processed */}
                {!document.isProcessingEndStateReached && !document.errorCode && (
                    <div className="flex min-h-full flex-col items-center justify-center py-12 text-center">
                        <Loading01 className="text-brand-solid size-10 shrink-0 animate-spin" aria-hidden />
                        <p className="mt-3 text-sm font-medium text-secondary">The document is being processed</p>
                    </div>
                )}

                {/* Branch 2: Error — processing incomplete or finished with error (vendor-lookup
                    and export failures are handled by their banners + editable fields, not this
                    centered layout) */}
                {!!document.errorCode && !isVendorLookupFailure && !isExportFailureMode && (
                    <div className="flex min-h-full flex-col items-center justify-center py-12 text-center">
                        <FeaturedIcon icon={AlertCircle} color="error" theme="light" size="md" />
                        <p className="mt-3 text-sm font-medium text-error-primary">{getErrorDisplayMessage(document.errorCode)}</p>
                        {document.errorCode === ExceptionCodeEnum.BizDuplicateFile && document.duplicateFileId != null && onSelectDuplicateFile && (
                            <button
                                type="button"
                                className="hover:text-brand-primary-hover mt-2 text-sm font-medium text-brand-primary underline"
                                onClick={() => onSelectDuplicateFile(document.duplicateFileId!)}
                            >
                                View existing file
                            </button>
                        )}
                    </div>
                )}

                {/* Branch 3: Normal — show editable extracted fields. Also shown in vendor-lookup
                    failure mode (where the lookup step stays Pending, so isProcessingEndStateReached
                    is false) so the user can correct the vendor name and re-run the lookup, and in
                    export-failure mode so the user can correct fields and retry the export. */}
                {isDetailReady && ((document.isProcessingEndStateReached && !document.errorCode) || isVendorLookupFailure || isExportFailureMode) && (
                    <>
                        <section>
                            <h3 className="mb-4 text-xs font-semibold tracking-wider text-quaternary uppercase">Extracted Fields</h3>
                            <div className="space-y-3">
                                {/* Organization — readonly display for multi-org users */}
                                {orgs && orgs.length > 1 && selectedOrgId != null && (
                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-secondary">Organization</label>
                                        <p className="text-sm text-primary">{orgs.find((o) => o.id === selectedOrgId)?.name ?? "Unknown"}</p>
                                    </div>
                                )}
                                {customFieldDefinitions
                                    .filter((d) =>
                                        // Failure mode: show ONLY the vendor-name field for correction.
                                        // Success/normal mode: show all visible fields (unchanged).
                                        isVendorLookupFailure ? d.fieldCode === (vendorNameFieldCode ?? "vendor_name") : d.isVisible,
                                    )
                                    .sort((a, b) => a.displayOrder - b.displayOrder)
                                    .map((definition) => (
                                        <EditableFieldRow
                                            key={definition.id}
                                            definition={definition}
                                            editedValue={editedFieldValues.get(definition.id)}
                                            hasError={fieldErrors.has(definition.id)}
                                            routingCodes={routingCodes}
                                            onChange={handleFieldChange}
                                        />
                                    ))}
                            </div>
                        </section>

                        {/* Line items — hidden in vendor-failure mode (only the vendor field shows there) */}
                        {!isVendorLookupFailure && (
                            <LineItemsSection
                                lineItems={lineItems}
                                definitions={lineItemFieldDefinitions}
                                editedValues={editedLineItemValues}
                                fieldErrors={lineItemFieldErrors}
                                routingCodes={routingCodes}
                                glCodes={glCodes}
                                glSuggestionsByLine={glSuggestionsByLine}
                                hasApplicableSuggestions={hasApplicableSuggestions}
                                onApplySuggestions={handleApplySuggestions}
                                onChange={handleLineItemFieldChange}
                            />
                        )}
                    </>
                )}
            </div>

            {/* ---- Actions (pinned bottom) ---- */}
            <div className="shrink-0 space-y-2 border-t border-tertiary px-5 py-4">
                {isVendorLookupFailure ? (
                    <>
                        {/* Vendor-lookup-failure mode: correct visible fields, then re-run the lookup.
                            Export is intentionally not shown until the lookup succeeds. */}
                        {relookupError && <p className="text-sm text-error-primary">{relookupError}</p>}
                        {updateError && <p className="text-sm text-error-primary">{updateError}</p>}
                        <Button
                            size="sm"
                            color="primary"
                            className="w-full"
                            iconLeading={RefreshCw05}
                            isDisabled={isRelooking || isUpdating}
                            isLoading={isRelooking}
                            onClick={handleRelookup}
                        >
                            Re-lookup
                        </Button>
                        <Button size="sm" color="secondary" className="w-full" isDisabled={isRelooking} onClick={handleCancelVendorEdits}>
                            Cancel
                        </Button>
                        <Button size="sm" color="secondary-destructive" className="w-full" iconLeading={Trash01} onClick={() => onDelete(document.id)}>
                            Delete
                        </Button>
                    </>
                ) : (
                    <>
                        {document.status === "queued" && <p className="text-sm text-brand-primary">Syncing to ERP… This may take a moment.</p>}
                        {document.status === "exported" && <p className="text-sm text-success-primary">Successfully exported.</p>}
                        {updateError && <p className="text-sm text-error-primary">{updateError}</p>}
                        {exportError && <p className="text-sm text-error-primary">{exportError}</p>}
                        <Button
                            size="sm"
                            color="primary"
                            className="w-full"
                            iconLeading={Save01}
                            isDisabled={!hasChanges || isUpdating}
                            isLoading={isUpdating}
                            onClick={handleUpdateFields}
                        >
                            Save
                        </Button>
                        <Button
                            size="sm"
                            color="primary"
                            className="w-full"
                            iconLeading={isExportFailureMode ? RefreshCw05 : FileCheck02}
                            isDisabled={!canExport}
                            isLoading={isSaving}
                            onClick={isAdminPreview ? () => setShowAdminExportWarning(true) : handleExport}
                        >
                            {isExportFailureMode && canDirectSync
                                ? "Retry export"
                                : document.status === "queued" || document.status === "exported"
                                  ? "Re-export"
                                  : "Export"}
                        </Button>
                        <Button size="sm" color="secondary-destructive" className="w-full" iconLeading={Trash01} onClick={() => onDelete(document.id)}>
                            Delete
                        </Button>
                    </>
                )}
            </div>

            {/* Admin export warning dialog */}
            <AdminExportWarningModal
                isOpen={showAdminExportWarning}
                onOpenChange={setShowAdminExportWarning}
                onConfirm={() => {
                    setShowAdminExportWarning(false);
                    handleExport();
                }}
            />
        </div>
    );
});

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Warning dialog shown to admins before exporting to a client's external system. */
function AdminExportWarningModal({ isOpen, onOpenChange, onConfirm }: { isOpen: boolean; onOpenChange: (isOpen: boolean) => void; onConfirm: () => void }) {
    return (
        <AriaDialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
            <ModalOverlay isDismissable>
                <Modal>
                    <Dialog>
                        <div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-100">
                            <CloseButton onClick={() => onOpenChange(false)} theme="light" size="lg" className="absolute top-3 right-3" />

                            <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
                                <div className="relative w-max">
                                    <FeaturedIcon color="warning" size="lg" theme="light" icon={AlertTriangle} />
                                    <BackgroundPattern pattern="circle" size="sm" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                                </div>

                                <div className="z-10 flex flex-col gap-0.5">
                                    <AriaHeading slot="title" className="text-md font-semibold text-primary">
                                        Export as admin
                                    </AriaHeading>
                                    <p className="text-sm text-tertiary">
                                        You are about to export a document to a client's external system. This may cause inconsistencies when it comes to client
                                        document traceability.
                                    </p>
                                </div>
                            </div>

                            <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                                <Button color="secondary" size="lg" onClick={() => onOpenChange(false)}>
                                    Cancel
                                </Button>
                                <Button color="primary" size="lg" onClick={onConfirm}>
                                    Export anyway
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </AriaDialogTrigger>
    );
}

/**
 * Line items with editable per-line custom fields.
 *
 * Renders inline in the review column: each line is a labelled block with its fields stacked
 * vertically (a wide table would not fit the narrow panel). The whole section collapses by
 * default when the line count is high so the document fields above stay reachable.
 */
function LineItemsSection({
    lineItems,
    definitions,
    editedValues,
    fieldErrors,
    routingCodes,
    glCodes,
    glSuggestionsByLine,
    hasApplicableSuggestions,
    onApplySuggestions,
    onChange,
}: {
    lineItems: LineItemDto[];
    definitions: CustomFieldDefinitionDto[];
    editedValues: Map<string, EditedField>;
    fieldErrors: Set<string>;
    routingCodes?: RoutingCodeDto[];
    glCodes?: GlCodeDto[];
    /** Ranked GL suggestions per line item id (best-first) */
    glSuggestionsByLine?: Map<number, GlSuggestionDto[]>;
    /** Whether at least one line has an empty GL field with a suggestion to apply */
    hasApplicableSuggestions?: boolean;
    /** Fill every empty GL field with its line's top suggestion (user still saves to confirm) */
    onApplySuggestions?: () => void;
    onChange: (lineItemId: number, definitionId: number, fieldType: string, value: EditedValue) => void;
}) {
    const [isExpanded, setIsExpanded] = useState(lineItems.length <= 3);

    const visibleDefinitions = useMemo(() => definitions.filter((d) => d.isVisible).sort((a, b) => a.displayOrder - b.displayOrder), [definitions]);

    if (lineItems.length === 0 || visibleDefinitions.length === 0) return null;

    return (
        <section>
            <button
                type="button"
                className="mb-4 flex w-full cursor-pointer items-center justify-between"
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded((prev) => !prev)}
            >
                <h3 className="text-xs font-semibold tracking-wider text-quaternary uppercase">Line Items ({lineItems.length})</h3>
                <ChevronDown className={cx("size-4 text-quaternary transition-transform duration-200", isExpanded && "rotate-180")} />
            </button>
            {isExpanded && (
                <div className="space-y-4">
                    {hasApplicableSuggestions && onApplySuggestions && (
                        <Button size="sm" color="secondary" className="w-full" onClick={onApplySuggestions}>
                            Apply suggested GL codes
                        </Button>
                    )}
                    {lineItems.map((lineItem) => (
                        <div key={lineItem.id} className="space-y-3 rounded-lg border border-tertiary p-3">
                            <p className="text-xs font-semibold tracking-wider text-quaternary uppercase">Line {lineItem.lineNumber}</p>
                            {visibleDefinitions.map((definition) => (
                                <EditableFieldRow
                                    key={definition.id}
                                    definition={definition}
                                    editedValue={editedValues.get(lineItemKey(lineItem.id, definition.id))}
                                    hasError={fieldErrors.has(lineItemKey(lineItem.id, definition.id))}
                                    routingCodes={routingCodes}
                                    glCodes={glCodes}
                                    glSuggestions={glSuggestionsByLine?.get(lineItem.id)}
                                    onChange={(definitionId, fieldType, value) => onChange(lineItem.id, definitionId, fieldType, value)}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

/**
 * Options for the gl_code Select: ranked suggestions first (marked with confidence),
 * then the rest of the chart of accounts, plus the current value if it is no longer
 * in either (stale codes must stay selectable so the stored value renders).
 */
function buildGlSelectItems(
    glSuggestions: GlSuggestionDto[] | undefined,
    glCodes: GlCodeDto[] | undefined,
    currentValue: string | null,
): { id: string; label: string; supportingText?: string }[] {
    const items: { id: string; label: string; supportingText?: string }[] = [];
    const seen = new Set<string>();
    for (const suggestion of glSuggestions ?? []) {
        if (seen.has(suggestion.code)) continue;
        seen.add(suggestion.code);
        items.push({
            id: suggestion.code,
            label: suggestion.code,
            supportingText: `Suggested · ${Math.round(suggestion.confidence * 100)}%`,
        });
    }
    for (const glCode of glCodes ?? []) {
        if (seen.has(glCode.code)) continue;
        seen.add(glCode.code);
        items.push({ id: glCode.code, label: glCode.code, supportingText: glCode.description ?? undefined });
    }
    if (currentValue && !seen.has(currentValue)) {
        items.unshift({ id: currentValue, label: currentValue });
    }
    return items;
}

/** Editable field row — renders the appropriate input based on field type. */
function EditableFieldRow({
    definition,
    editedValue,
    hasError,
    routingCodes,
    glCodes,
    glSuggestions,
    onChange,
}: {
    definition: CustomFieldDefinitionDto;
    editedValue: EditedField | undefined;
    hasError: boolean;
    routingCodes?: RoutingCodeDto[];
    glCodes?: GlCodeDto[];
    /** Ranked suggestions for THIS line's GL field (best-first) */
    glSuggestions?: GlSuggestionDto[];
    onChange: (definitionId: number, fieldType: string, value: EditedValue) => void;
}) {
    const { id, fieldLabel: label, fieldType, fieldCode, isRequired } = definition;
    const currentValue = editedValue?.value ?? null;

    const isGlCodeSelect = fieldCode === GL_CODE_FIELD_CODE && ((glSuggestions?.length ?? 0) > 0 || (glCodes?.length ?? 0) > 0);
    const glSelectItems = useMemo(
        () => (isGlCodeSelect ? buildGlSelectItems(glSuggestions, glCodes, (currentValue as string) ?? null) : []),
        [isGlCodeSelect, glSuggestions, glCodes, currentValue],
    );

    return (
        <div>
            <label className="mb-1 block text-xs font-medium tracking-wider text-quaternary uppercase">
                {label}
                {isRequired && <span className="ml-1 text-error-primary">*</span>}
            </label>

            {(fieldType === FieldTypeEnum.String || fieldType === "String") &&
                (fieldCode === "routing_code" && routingCodes && routingCodes.length > 0 ? (
                    <Select
                        selectedKey={(currentValue as string) ?? null}
                        onSelectionChange={(key) => onChange(id, fieldType, key as string)}
                        size="sm"
                        placeholder="Select routing code"
                        items={routingCodes.map((rc) => ({ id: rc.code, label: rc.code }))}
                    >
                        {(item) => <Select.Item id={item.id} label={item.label} />}
                    </Select>
                ) : isGlCodeSelect ? (
                    <Select
                        selectedKey={(currentValue as string) ?? null}
                        onSelectionChange={(key) => onChange(id, fieldType, key as string)}
                        size="sm"
                        placeholder={glSuggestions?.[0] ? `Suggested: ${glSuggestions[0].code}` : "Select GL code"}
                        items={glSelectItems}
                    >
                        {(item) => <Select.Item id={item.id} label={item.label} supportingText={item.supportingText} />}
                    </Select>
                ) : (
                    <Input
                        value={(currentValue as string) ?? ""}
                        onChange={(value) => onChange(id, fieldType, value)}
                        size="sm"
                        placeholder={`Enter ${label.toLowerCase()}`}
                        isInvalid={hasError}
                    />
                ))}

            {(fieldType === FieldTypeEnum.Integer || fieldType === "Integer") && (
                <Input
                    type="number"
                    value={currentValue !== null ? String(currentValue) : ""}
                    onChange={(value) => {
                        const cleanValue = value.replace(/[^0-9-]/g, "");
                        const numValue = cleanValue === "" ? null : parseInt(cleanValue, 10);
                        if (numValue !== null && !isNaN(numValue)) {
                            onChange(id, fieldType, numValue);
                        } else if (cleanValue === "" || cleanValue === "-") {
                            onChange(id, fieldType, null);
                        }
                    }}
                    size="sm"
                    placeholder={`Enter ${label.toLowerCase()}`}
                    isInvalid={hasError}
                />
            )}

            {(fieldType === FieldTypeEnum.Decimal || fieldType === "Decimal") && (
                <Input
                    type="number"
                    value={currentValue !== null ? String(currentValue) : ""}
                    onChange={(value) => {
                        const numValue = value === "" ? null : parseFloat(value);
                        if (numValue !== null && !isNaN(numValue)) {
                            onChange(id, fieldType, numValue);
                        } else if (value === "") {
                            onChange(id, fieldType, null);
                        }
                    }}
                    size="sm"
                    placeholder={`Enter ${label.toLowerCase()}`}
                    isInvalid={hasError}
                />
            )}

            {(fieldType === FieldTypeEnum.Boolean || fieldType === "Boolean") && (
                <div className={hasError ? "border-error-primary rounded border p-1" : ""}>
                    <Checkbox isSelected={currentValue === true} onChange={(isSelected) => onChange(id, fieldType, isSelected)} size="sm" />
                </div>
            )}

            {(fieldType === FieldTypeEnum.Date || fieldType === "Date") && (
                <DatePicker
                    value={currentValue && typeof currentValue === "object" && "toDate" in currentValue ? (currentValue as DateValue) : null}
                    onChange={(value) => onChange(id, fieldType, value)}
                    isInvalid={hasError}
                />
            )}
        </div>
    );
}
